import React, { createContext, useContext, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { analytics } from '@/lib/analytics';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, XCircle, Settings } from 'lucide-react';

interface ConsentContextType {
  hasConsent: boolean;
  showBanner: boolean;
  giveConsent: () => void;
  revokeConsent: () => void;
  dismissBanner: () => void;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within ConsentProvider');
  }
  return context;
};

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasConsent, setHasConsent] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const initConsent = async () => {
      // S3-SECURITY-FIX: Check for consent banner disable flag
      // This prevents accidental data collection in development/testing environments
      const disableBanner = import.meta.env.VITE_DISABLE_CONSENT_BANNER === 'true' 
        || import.meta.env.MODE === 'test';
      
      if (disableBanner) {
        // When banner is disabled, default to NO consent for safety
        setHasConsent(false);
        setShowBanner(false);
        if (import.meta.env.DEV) {
          console.debug('[ConsentBanner] Banner disabled via env flag - defaulting to no consent');
        }
        return;
      }

      try {
        // Check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          // ✅ Priority 1: Check database for authenticated users
          // PHASE 2: Use maybeSingle() to handle missing records gracefully (fixes 406 error)
          const { data: dbConsent } = await supabase
            .from('user_consents')
            .select('consent_given, withdrawal_date')
            .eq('user_id', session.user.id)
            .eq('consent_type', 'analytics')
            .maybeSingle();
          
          if (dbConsent && dbConsent.consent_given && !dbConsent.withdrawal_date) {
            setHasConsent(true);
            analytics.giveConsent();
            return; // Don't check localStorage
          }
        }
      } catch (error) {
        console.warn('[ConsentBanner] Database check failed:', error);
      }
      
      // ✅ Priority 2: Fallback to localStorage
      const storedConsent = localStorage.getItem('analytics-consent');
      const bannerDismissed = localStorage.getItem('analytics-banner-dismissed');
      
      if (storedConsent === 'accepted') {
        setHasConsent(true);
        analytics.giveConsent();
      } else if (storedConsent === 'rejected') {
        setHasConsent(false);
      } else if (!bannerDismissed) {
        // Show banner for first-time visitors
        setShowBanner(true);
        
        // Track banner shown (without consent)
        if (import.meta.env.DEV) {
          console.debug('[ConsentBanner] Banner shown to new visitor');
        }
      }
    };
    
    initConsent();
  }, []);

  const giveConsent = async () => {
    const correlationId = crypto.randomUUID();
    console.log(`[ConsentBanner:${correlationId}] Starting consent acceptance`);
    setIsSaving(true);
    
    try {
      // DEFENSIVE: Write to localStorage FIRST (backward compatibility)
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          localStorage.setItem('analytics-consent', 'accepted');
          localStorage.removeItem('analytics-banner-dismissed');
          
          if (import.meta.env.DEV) {
            console.debug(`[ConsentBanner:${correlationId}] Consent persisted to localStorage`);
          }
        } catch (error) {
          console.error(`[ConsentBanner:${correlationId}] Failed to save consent preference:`, error);
          setIsSaving(false);
          return;
        }
      }
      
      // ✅ NEW: Persist to database for authenticated users
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { error: dbError } = await supabase
          .from('user_consents')
          .upsert({
            user_id: session.user.id,
            consent_type: 'analytics',
            consent_given: true,
            consent_date: new Date().toISOString(),
            purpose: 'Improve our services through usage analytics',
            legal_basis: 'consent',
            data_categories: ['usage', 'technical'],
            retention_period: '2 years'
          }, {
            onConflict: 'user_id,consent_type',
            ignoreDuplicates: false
          });
        
        if (dbError) {
          console.error(`[ConsentBanner:${correlationId}] Failed to persist consent to database:`, dbError);
          // Don't block UI - localStorage fallback is active
        } else {
          if (import.meta.env.DEV) {
            console.debug(`[ConsentBanner:${correlationId}] Consent persisted to database`);
          }
        }
      }
      
      setHasConsent(true);
      setShowBanner(false);
      
      // Initialize analytics
      await analytics.giveConsent();
      await analytics.track('consent_accepted', {
        timestamp: Date.now(),
        source: 'banner'
      });
      
      console.log(`[ConsentBanner:${correlationId}] Consent acceptance complete`);
    } catch (error) {
      console.error(`[ConsentBanner:${correlationId}] Consent save failed:`, error);
    } finally {
      setIsSaving(false);
    }
  };

  const revokeConsent = async () => {
    // ✅ NEW: Persist revocation to database
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      await supabase
        .from('user_consents')
        .upsert({
          user_id: session.user.id,
          consent_type: 'analytics',
          consent_given: false,
          withdrawal_date: new Date().toISOString(),
          purpose: 'Improve our services through usage analytics',
          legal_basis: 'consent',
          data_categories: ['usage', 'technical'],
          retention_period: '2 years'
        }, {
          onConflict: 'user_id,consent_type'
        });
    }
    
    setHasConsent(false);
    analytics.revokeConsent();
    
    if (import.meta.env.DEV) {
      console.debug('[ConsentBanner] Consent revoked');
    }
  };

  const dismissBanner = () => {
    setShowBanner(false);
    try {
      localStorage.setItem('analytics-banner-dismissed', 'true');
    } catch (error) {
      console.warn('[ConsentBanner] Failed to persist banner dismissal', error);
    }
    
    // Track banner dismissal (declined consent)
    if (import.meta.env.DEV) {
      console.debug('[ConsentBanner] Banner dismissed (consent declined)');
    }
  };

  return (
    <ConsentContext.Provider value={{
      hasConsent,
      showBanner,
      giveConsent,
      revokeConsent,
      dismissBanner
    }}>
      {children}
      {showBanner && (
        <ErrorBoundary
          fallback={
            <div className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background">
              <div className="container mx-auto px-4 py-2">
                <p className="text-xs text-muted-foreground">Unable to load privacy notice</p>
              </div>
            </div>
          }
        >
          <ConsentBanner isSaving={isSaving} />
        </ErrorBoundary>
      )}
    </ConsentContext.Provider>
  );
};

export const ConsentBanner: React.FC<{ isSaving?: boolean }> = ({ isSaving = false }) => {
  const { giveConsent, dismissBanner } = useConsent();

  return (
    <div 
      role="region" 
      aria-live="polite" 
      aria-label="Privacy notice banner"
      className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background shadow-lg"
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm text-foreground">
              This website uses cookies to supplement a balanced diet and provide a much deserved reward to the senses after consuming bland but nutritious meals. Accepting our cookies is optional but recommended, as they are delicious. See our{" "}
              <a href="/privacy" className="text-primary hover:underline">
                cookie policy
              </a>.
            </p>
          </div>
          
          <div className="flex items-center gap-3 flex-shrink-0">
            <Button 
              onClick={giveConsent} 
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Accept all'}
            </Button>
            <Button 
              variant="ghost" 
              onClick={dismissBanner} 
              disabled={isSaving}
            >
              Reject all
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ConsentSettings: React.FC = () => {
  const { hasConsent, giveConsent, revokeConsent } = useConsent();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="w-4 h-4" />
          Privacy & Analytics
        </CardTitle>
        <CardDescription>
          Manage your data collection and analytics preferences
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {hasConsent ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <XCircle className="w-4 h-4 text-red-600" />
            )}
            <span className="font-medium">Analytics Tracking</span>
            <Badge variant={hasConsent ? "default" : "secondary"}>
              {hasConsent ? "Enabled" : "Disabled"}
            </Badge>
          </div>
          <Button
            size="sm"
            variant={hasConsent ? "destructive" : "default"}
            onClick={hasConsent ? revokeConsent : giveConsent}
          >
            {hasConsent ? "Disable" : "Enable"}
          </Button>
        </div>
        
        <div className="text-xs text-muted-foreground space-y-2">
          <p>
            <strong>What we collect:</strong> Page views, feature usage, and error reports.
            All data is anonymized using secure hashing.
          </p>
          <p>
            <strong>What we don't collect:</strong> Personal information, form data, 
            file contents, or any identifiable information.
          </p>
          <p>
            <strong>Data retention:</strong> Analytics data is automatically purged 
            after 90 days for privacy compliance.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
